import { useQuery } from '@tanstack/react-query'
import { jsonFetch } from '@/lib/api/jsonFetch'
import type {
  ActivityEntry,
  CofaPipelineCount,
  CofaStats,
  OpenOrdersCount,
  PipelineAttentionItem,
  RiskSummary,
} from './types'

export function useOpenOrdersCount() {
  return useQuery({
    queryKey: ['dashboard', 'orders', 'count'],
    queryFn: () => jsonFetch<OpenOrdersCount>('/api/open-orders/count'),
    refetchInterval: 60_000,
    staleTime: 30_000,
  })
}

export function useCofaPipelineCount() {
  return useQuery({
    queryKey: ['dashboard', 'cofa-pipeline', 'count'],
    queryFn: () =>
      jsonFetch<CofaPipelineCount>('/api/procurement/cofa-pipeline/count'),
    refetchInterval: 120_000,
    staleTime: 60_000,
  })
}

export function useCofaStatsSnapshot() {
  return useQuery({
    queryKey: ['dashboard', 'cofa-stats'],
    queryFn: () => jsonFetch<CofaStats>('/api/procurement/cofa-stats'),
    refetchInterval: 300_000,
    staleTime: 120_000,
  })
}

export function useFulfillmentRiskSummary() {
  return useQuery({
    queryKey: ['dashboard', 'fulfillment', 'summary'],
    queryFn: async () => {
      const data = await jsonFetch<{
        commercial?: { summary?: RiskSummary }
      }>('/api/fulfillment/risk')
      return data?.commercial?.summary ?? null
    },
    refetchInterval: 180_000,
    staleTime: 90_000,
  })
}

export function useActivityFeed(limit = 20) {
  return useQuery({
    queryKey: ['dashboard', 'activity', limit],
    queryFn: async () => {
      const data = await jsonFetch<{ actions?: ActivityEntry[] }>(
        `/api/actions/history?limit=${limit}`,
      )
      return data?.actions ?? []
    },
    refetchInterval: 90_000,
    staleTime: 30_000,
  })
}

export function useCofaAttention(limit = 8) {
  return useQuery({
    queryKey: ['dashboard', 'attention', 'cofa', limit],
    queryFn: async () => {
      const data = await jsonFetch<{
        items?: Array<{
          id: number
          lot_name: string
          product_name: string | null
          vendor_name: string | null
          stage: string
          age_seconds: number | null
        }>
      }>(
        `/api/procurement/cofa-pipeline?stage=awaiting_approval&limit=${limit}`,
      )
      return (data?.items ?? []) as PipelineAttentionItem[]
    },
    refetchInterval: 120_000,
    staleTime: 60_000,
  })
}

export interface InventoryAttentionRow {
  product_id: number
  product_name: string
  qty_available: number
  qty_on_hand: number
  uom: string
}

export interface ExpiringAttentionRow {
  lot_name: string
  product: string
  product_id: number
  qty_on_hand: number
  expiration_date: string | null
}

export function useInventoryAttention() {
  return useQuery({
    queryKey: ['dashboard', 'attention', 'inventory'],
    queryFn: async () => {
      const data = await jsonFetch<{
        stock_report?: { products?: InventoryAttentionRow[] }
        expiring_lots?: { lots?: ExpiringAttentionRow[] }
      }>('/api/reports/inventory?limit=500')
      const oos = (data?.stock_report?.products ?? [])
        .filter((p) => p.qty_available <= 0)
        .slice(0, 8)
      const today = new Date().toISOString().slice(0, 10)
      const cutoff = new Date(today)
      cutoff.setDate(cutoff.getDate() + 30)
      const cutoffIso = cutoff.toISOString().slice(0, 10)
      const expiring = (data?.expiring_lots?.lots ?? [])
        .filter(
          (l) =>
            l.expiration_date &&
            l.expiration_date >= today &&
            l.expiration_date <= cutoffIso,
        )
        .slice(0, 8)
      return { oos, expiring }
    },
    refetchInterval: 300_000,
    staleTime: 120_000,
  })
}

export interface OverduePORow {
  po_id: number
  po_number: string
  vendor: string
  expected_receipt: string | null
  product_name: string
  qty_open: number
  uom: string
}

export function usePOAttention() {
  return useQuery({
    queryKey: ['dashboard', 'attention', 'pos'],
    queryFn: async () => {
      const data = await jsonFetch<{
        rows?: OverduePORow[]
      }>('/api/reports/vendor-pos')
      const today = new Date().toISOString().slice(0, 10)
      return (data?.rows ?? [])
        .filter((r) => r.expected_receipt && r.expected_receipt < today)
        .sort((a, b) =>
          (a.expected_receipt ?? '').localeCompare(b.expected_receipt ?? ''),
        )
        .slice(0, 8)
    },
    refetchInterval: 180_000,
    staleTime: 90_000,
  })
}

export interface OpenOrderAttentionRow {
  sol_id: number
  customer: string
  so_number: string
  so_id: number
  product_name: string
  due_on: string | null
  open: number
  status: string
  coverage_status: string | null
}

export function useOrderAttention() {
  return useQuery({
    queryKey: ['dashboard', 'attention', 'orders'],
    queryFn: async () => {
      const yearStart = `${new Date().getFullYear()}-01-01`
      const today = new Date().toISOString().slice(0, 10)
      const data = await jsonFetch<{ rows?: OpenOrderAttentionRow[] }>(
        '/api/open-orders/detailed',
        {
          method: 'POST',
          body: {
            date_from: yearStart,
            date_to: today,
            rules: { outstanding_only: true, overdue_only: true },
            exclusions: {},
          },
        },
      )
      return (data?.rows ?? [])
        .sort((a, b) => (a.due_on ?? '').localeCompare(b.due_on ?? ''))
        .slice(0, 8)
    },
    refetchInterval: 300_000,
    staleTime: 120_000,
  })
}
